import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { getPricing, isBlockingFlag, money } from './rateEngine';
import { inPeriod, manifestDay } from './payroll';
import { EmptyState, FlagBadge, Kpi, PeriodPicker } from './shared';

const STATUS_FILTERS = [
  ['open', 'Open'],
  ['reviewed', 'Reviewed'],
  ['all', 'All'],
];

/**
 * Screen 4 — Exception Audits
 * Every flagged piece for the week. Billing exceptions (weight/dims) are approved or waived;
 * rate-card exceptions (unmapped FSA, zone N/A) are fixed on the rate card or accepted at $0.
 * A manifest can't be posted until all of its exceptions are reviewed.
 */
const ExceptionAudits = ({ period, manifests, cards, onReview, onOpenManifest, onOpenRateCard }) => {
  const [status, setStatus] = useState('open');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const all = useMemo(
    () =>
      manifests
        .filter((m) => inPeriod(manifestDay(m), period.start, period.cfg))
        .flatMap((m) =>
          getPricing(m, cards).exceptions.map((p) => ({
            ...p,
            key: `${m.id}|${p.barcode}`,
            manifest: m,
            blocking: p.flags.some(isBlockingFlag),
            locked: m.status === 'posted',
          }))
        ),
    [manifests, cards, period.start, period.cfg]
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((r) => {
      if (status === 'open' && r.review) return false;
      if (status === 'reviewed' && !r.review) return false;
      if (!q) return true;
      return [r.name, r.address, r.fsa, r.barcode, r.manifest.route].some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [all, status, search]);

  const open = all.filter((r) => !r.review);
  const selectable = rows.filter((r) => !r.locked && !r.review);
  const selectedRows = rows.filter((r) => selected.includes(r.key));

  const toggle = (key) => setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  /** Group by manifest so each manifest is updated once with all its barcodes. */
  const review = async (items, decision) => {
    setBusy(true);
    try {
      const byManifest = new Map();
      items.forEach((r) => byManifest.set(r.manifest.id, [...(byManifest.get(r.manifest.id) || []), r]));
      for (const group of byManifest.values()) {
        await onReview(group[0].manifest, group.map((r) => r.barcode), decision, note);
      }
      setSelected([]);
      setNote('');
    } finally {
      setBusy(false);
    }
  };

  const selectedBlocking = selectedRows.some((r) => r.blocking);

  return (
    <div className="space-y-6" id="exception-audits-screen">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">Exception Audits</h2>
          <p className="text-sm text-muted-foreground">Review flagged pieces before manifests are posted to the GL.</p>
        </div>
        <PeriodPicker {...period} idPrefix="exceptions" title="Week" />
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi label="Open Exceptions" value={open.length} icon="fa-exclamation-triangle" accent={open.length ? 'text-amber-500' : 'text-emerald-500'} />
        <Kpi label="Reviewed" value={all.length - open.length} sub={`of ${all.length}`} icon="fa-check-double" />
        <Kpi label="Accessorials at Stake" value={money(open.filter((r) => !r.blocking).reduce((s, r) => s + r.accessorial, 0))} sub="open weight/dims charges" icon="fa-dollar-sign" />
        <Kpi
          label="Unbillable Pieces"
          value={open.filter((r) => r.blocking).length}
          sub="unmapped FSA / zone N/A"
          icon="fa-map-marker-alt"
          accent={open.some((r) => r.blocking) ? 'text-red-500' : 'text-foreground'}
        />
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="pb-3 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm"></i>
              <Input id="exceptions-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by route, name, FSA or barcode…" className="pl-9" />
            </div>
            <div className="flex gap-2">
              {STATUS_FILTERS.map(([key, label]) => (
                <Button
                  key={key}
                  id={`exceptions-filter-${key}`}
                  size="sm"
                  variant={status === key ? 'default' : 'outline'}
                  onClick={() => {
                    setStatus(key);
                    setSelected([]);
                  }}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
          {selectedRows.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 p-2 rounded-md bg-muted/50 border border-border">
              <span className="text-sm font-medium mr-2">{selectedRows.length} selected</span>
              <Input id="exceptions-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Audit note (optional)" className="h-8 max-w-[280px]" />
              <Button id="exceptions-bulk-approve" size="sm" disabled={busy || selectedBlocking} className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => review(selectedRows, 'approved')}>
                Approve charges
              </Button>
              <Button id="exceptions-bulk-waive" size="sm" variant="outline" disabled={busy} onClick={() => review(selectedRows, 'waived')}>
                Waive / bill base rate
              </Button>
              {selectedBlocking && <span className="text-xs text-muted-foreground">Unbillable pieces can only be waived (accepted at $0) or fixed on the rate card.</span>}
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="p-6">
              <EmptyState icon="fa-check-circle" title={status === 'open' ? 'No open exceptions this week' : 'Nothing to show'} />
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[560px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/80 backdrop-blur text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="p-3 w-8">
                      <input
                        id="exceptions-select-all"
                        type="checkbox"
                        checked={selectable.length > 0 && selectable.every((r) => selected.includes(r.key))}
                        onChange={(e) => setSelected(e.target.checked ? selectable.map((r) => r.key) : [])}
                      />
                    </th>
                    <th className="text-left p-3">Manifest</th>
                    <th className="text-left p-3">Recipient</th>
                    <th className="text-left p-3">FSA / Zone</th>
                    <th className="text-right p-3">Weight</th>
                    <th className="text-left p-3">Flags</th>
                    <th className="text-right p-3">Billed</th>
                    <th className="text-left p-3">Review</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key} className="border-t border-border hover:bg-muted/30">
                      <td className="p-3">
                        {!r.locked && !r.review && <input type="checkbox" checked={selected.includes(r.key)} onChange={() => toggle(r.key)} />}
                      </td>
                      <td className="p-3">
                        <button className="font-mono text-sm hover:text-emerald-500" onClick={() => onOpenManifest(r.manifest.id)}>
                          {r.manifest.route}
                        </button>
                        <div className="text-xs text-muted-foreground">
                          {manifestDay(r.manifest)} · {r.barcode}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="capitalize">{r.name}</div>
                        <div className="text-xs text-muted-foreground">{r.address}</div>
                      </td>
                      <td className="p-3 font-mono">
                        {r.fsa || '—'} / {r.zone ?? '—'}
                      </td>
                      <td className="p-3 text-right">{r.weightLb.toFixed(2)} lb</td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {r.flags.map((f) => (
                            <FlagBadge key={f} flag={f} label={r.flagLabels?.[f]} />
                          ))}
                        </div>
                      </td>
                      <td className="p-3 text-right font-semibold">{money(r.customer)}</td>
                      <td className="p-3">
                        {r.review ? (
                          <div>
                            <Badge variant="outline" className={r.review.decision === 'approved' ? 'border-emerald-500/40 text-emerald-500' : 'border-sky-500/40 text-sky-500'}>
                              {r.review.decision}
                            </Badge>
                            <div className="text-[11px] text-muted-foreground mt-1">
                              {r.review.by}
                              {r.review.note ? ` · ${r.review.note}` : ''}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-amber-500">Open</span>
                        )}
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        {r.locked ? (
                          <span className="text-xs text-muted-foreground">Posted</span>
                        ) : r.review ? (
                          <Button size="sm" variant="ghost" disabled={busy} onClick={() => review([r], null)}>
                            Reopen
                          </Button>
                        ) : r.blocking ? (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="outline" onClick={onOpenRateCard}>
                              Map FSA
                            </Button>
                            <Button size="sm" variant="ghost" disabled={busy} onClick={() => review([r], 'waived')}>
                              Accept $0
                            </Button>
                          </div>
                        ) : (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => review([r], 'approved')}>
                              Approve
                            </Button>
                            <Button size="sm" variant="outline" disabled={busy} onClick={() => review([r], 'waived')}>
                              Waive
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExceptionAudits;
